import React, { useState } from "react";
import {
  Input,
  Select,
  Button,
  Popconfirm,
  Tag,
  Space,
  Segmented,
  Modal,
} from "antd";
import {
  DeleteOutlined,
  SaveOutlined,
  EditOutlined,
  CloseOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import CommonTable from "../../../components/shared/table/Table";
import "../Quiz.css";

const { Option } = Select;

const ParsedQuestionsTable = ({
  questions,
  setQuestions,
  onSubmit,
  onCancel,
  validationErrors = {},
  bulkLoading = false,
  hasApiErrors = false,
}) => {
  const [editingKey, setEditingKey] = useState("");
  const [editForm, setEditForm] = useState({});
  const [view, setView] = useState("all");
  const [errorModalKey, setErrorModalKey] = useState(null);

  const isEditing = (record) =>
    record.key === editingKey || record.rowNumber === editingKey;

  const startEdit = (record) => {
    const key = record.key || record.rowNumber;
    setEditingKey(key);
    setEditForm({ ...record });
  };

  const cancelEdit = () => {
    setEditingKey("");
    setEditForm({});
  };

  const handleFieldChange = (field, value) => {
    setEditForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const saveRow = (key) => {
    const updatedData = questions.map((item, index) => {
      const itemKey = item.key || item.rowNumber || index + 1;
      if (itemKey === key) {
        return { ...editForm, key: itemKey };
      }
      return item;
    });
    setQuestions(updatedData);
    setEditingKey("");
    setEditForm({});
  };

  const deleteRow = (key) => {
    const filteredData = questions.filter(
      (item, index) => (item.key || item.rowNumber || index + 1) !== key,
    );

    const reindexedData = filteredData.map((item, index) => ({
      ...item,
      key: index + 1,
      rowNumber: index + 1,
    }));

    setQuestions(reindexedData);
  };

  const baseColumns = [
    {
      title: "Type",
      dataIndex: "questionType",
      width: 160,
      render: (text, record) => {
        if (isEditing(record)) {
          return (
            <Select
              value={editForm.questionType}
              onChange={(val) => handleFieldChange("questionType", val)}
              style={{ width: "100%" }}
            >
              <Option value="MCQ_SINGLE">Single (MCQ_SINGLE)</Option>
              <Option value="MCQ_MULTIPLE">Multiple (MCQ_MULTIPLE)</Option>
              <Option value="SHORT_ANSWER">Short Answer (SHORT_ANSWER)</Option>
            </Select>
          );
        }
        return (
          <Tag
            color={
              text === "MCQ_SINGLE" || text === "single"
                ? "blue"
                : text === "MCQ_MULTIPLE" || text === "multiple"
                  ? "purple"
                  : "orange"
            }
          >
            {text?.toUpperCase() || "MCQ_SINGLE"}
          </Tag>
        );
      },
    },
    {
      title: "Question Text",
      dataIndex: "questionText",
      width: 250,
      render: (text, record) => {
        if (isEditing(record)) {
          return (
            <Input.TextArea
              rows={2}
              value={editForm.questionText}
              onChange={(e) =>
                handleFieldChange("questionText", e.target.value)
              }
            />
          );
        }
        return (
          text || <span className="text-gray-400 italic">No Question Text</span>
        );
      },
    },
    {
      title: "Points",
      dataIndex: "points",
      width: 80,
      render: (text, record) => {
        if (isEditing(record)) {
          return (
            <Input
              type="number"
              value={editForm.points}
              onChange={(e) => handleFieldChange("points", e.target.value)}
            />
          );
        }
        return text || "0";
      },
    },
    ...[1, 2, 3, 4, 5].map((optIndex) => ({
      title: `Option ${optIndex}`,
      dataIndex: `option${optIndex}`,
      width: 150,
      render: (text, record) => {
        if (isEditing(record)) {
          return (
            <Input.TextArea
              rows={2}
              value={editForm[`option${optIndex}`]}
              onChange={(e) =>
                handleFieldChange(`option${optIndex}`, e.target.value)
              }
              disabled={editForm.questionType === "SHORT_ANSWER"}
            />
          );
        }
        return text || "-";
      },
    })),
    {
      title: "Correct Answer",
      dataIndex: "correctAnswer",
      width: 220,
      render: (text, record) => {
        const isShortAnswer =
          (isEditing(record) ? editForm.questionType : record.questionType) ===
          "SHORT_ANSWER";

        if (isEditing(record)) {
          return isShortAnswer ? (
            <Input.TextArea
              rows={2}
              autoSize={{ minRows: 2, maxRows: 6 }}
              placeholder="Enter answer..."
              value={editForm.correctAnswer}
              onChange={(e) =>
                handleFieldChange("correctAnswer", e.target.value)
              }
              className="w-full text-xs"
            />
          ) : (
            <Input
              placeholder={
                editForm.questionType === "MCQ_MULTIPLE" ? "e.g. 1,3" : "e.g. 1"
              }
              value={editForm.correctAnswer}
              onChange={(e) =>
                handleFieldChange("correctAnswer", e.target.value)
              }
            />
          );
        }

        return isShortAnswer ? (
          <div className="whitespace-normal break-words max-h-[120px] overflow-y-auto text-xs p-1.5 border border-[#b7eb8f] rounded bg-[#f6ffed] text-[#389e0d]">
            {text || "-"}
          </div>
        ) : (
          <Tag color="green">{text || "-"}</Tag>
        );
      },
    },
  ];

  const errorColumn = {
    title: "Status",
    key: "status",
    width: 120,
    fixed: "left",
    render: (_, record, index) => {
      const rowKey = record.key || record.rowNumber || index + 1;
      const errs = validationErrors[rowKey] || [];

      if (errs.length === 0) {
        return (
          <Tag color="success" icon={<CheckCircleOutlined />} className="!m-0">
            Valid
          </Tag>
        );
      }

      return (
        <Tag
          color="error"
          icon={<ExclamationCircleOutlined />}
          className="!m-0 cursor-pointer select-none"
          onClick={() => setErrorModalKey(rowKey)}
        >
          {errs.length} {errs.length === 1 ? "issue" : "issues"}
        </Tag>
      );
    },
  };

  const actionColumn = {
    title: "Actions",
    key: "action",
    fixed: "right",
    width: 120,
    render: (_, record, index) => {
      const rowKey = record.key || record.rowNumber || index + 1;
      const editable = isEditing(record);

      return editable ? (
        <Space size="small">
          <Button
            type="primary"
            size="small"
            icon={<SaveOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              saveRow(rowKey);
            }}
          />
          <Button
            size="small"
            icon={<CloseOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              cancelEdit();
            }}
          />
        </Space>
      ) : (
        <Space size="small">
          <Button
            type="text"
            size="small"
            disabled={editingKey !== ""}
            icon={<EditOutlined className="text-blue-600" />}
            onClick={(e) => {
              e.stopPropagation();
              startEdit(record);
            }}
          />
          <Popconfirm
            title="Delete question?"
            onConfirm={() => deleteRow(rowKey)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="text"
              danger
              size="small"
              disabled={editingKey !== ""}
              icon={<DeleteOutlined />}
              onClick={(e) => e.stopPropagation()}
            />
          </Popconfirm>
        </Space>
      );
    },
  };

  const [indexColumn, ...restColumns] = baseColumns;

  const columns = [
    indexColumn,
    ...(hasApiErrors ? [errorColumn] : []),
    ...restColumns,
    actionColumn,
  ];

  const normalizedData = questions.map((item, index) => ({
    ...item,
    key: item.key || item.rowNumber || index + 1,
  }));

  const hasRowErrors = (key) => validationErrors[key]?.length > 0;
  const errorRowCount = normalizedData.filter((r) =>
    hasRowErrors(r.key),
  ).length;

  const visibleData =
    hasApiErrors && view === "errors"
      ? normalizedData.filter((r) => hasRowErrors(r.key))
      : normalizedData;

  return (
    <div className="w-full flex flex-col gap-4 my-4 p-4 border border-gray-200 rounded-lg bg-white shadow-sm">
      {hasApiErrors && (
        <div className="flex items-center justify-between rounded-md border border-red-200 bg-red-50 px-4 py-2.5">
          <div className="flex items-center gap-2 text-sm text-red-800">
            <ExclamationCircleOutlined />
            <span>
              <b>{errorRowCount}</b> of {normalizedData.length} questions need
              fixing. Edit the rows below and save again.
            </span>
          </div>
          <Segmented
            size="small"
            value={view}
            onChange={setView}
            options={[
              { label: "All", value: "all" },
              { label: `Errors (${errorRowCount})`, value: "errors" },
            ]}
          />
        </div>
      )}

      <CommonTable
        data={visibleData}
        columns={columns}
        rowKey="key"
        scroll={{ x: 1600, y: 600 }}
        showLoader={bulkLoading}
        rowClassName={(record) =>
          hasRowErrors(record.key) ? "parsed-row-error" : ""
        }
      />

      <div className="flex justify-end gap-3 mt-2">
        <Button onClick={onCancel} disabled={editingKey !== "" || bulkLoading}>
          Cancel / Exit
        </Button>
        <Button
          type="primary"
          onClick={() => onSubmit(questions)}
          loading={bulkLoading}
          disabled={editingKey !== "" || questions.length === 0}
          className="bg-[#910E11] hover:bg-[#700b0d]"
        >
          Save All Questions
        </Button>
      </div>

      <Modal
        open={errorModalKey !== null}
        onCancel={() => setErrorModalKey(null)}
        footer={null}
        title={`Row ${errorModalKey} errors`}
      >
        <div className="flex max-h-[50vh] flex-col gap-2 overflow-y-auto">
          {(validationErrors[errorModalKey] || []).map((err, i) => (
            <div
              key={i}
              className="flex items-start gap-2 text-sm text-red-700"
            >
              <ExclamationCircleOutlined className="mt-1 shrink-0" />
              <span>{err}</span>
            </div>
          ))}
        </div>
      </Modal>
    </div>
  );
};

export default ParsedQuestionsTable;
